import { createContext, useContext, type ReactNode } from "react"
import type {
  ToolLoadingRenderer,
  ToolRenderer,
  ToolRendererOptions,
} from "../blocks/agent-chat/tool-registry/types"

/**
 * Tool-renderer registry as React context. Replaces the
 * module-scoped singleton in `agent-chat/tool-registry` for hosts
 * that prefer explicit prop-driven registration (no side-effect
 * imports, no tree-shake hacks, no `window.__TOOL_CARD_COUNT__`).
 *
 * `ToolCallCard` (in both `agent-chat` and `chat` blocks) checks
 * this context FIRST and falls back to the module registry. So:
 *   - Existing `agent-chat` consumers that call `registerTool(...)`
 *     keep working unchanged.
 *   - New `chat` block consumers wrap with `ToolRendererProvider`
 *     and pass plain objects — no side effects, bundler-safe.
 *
 * Multi-alias renderers (e.g. `getBookings` + `get-bookings`)
 * just repeat the entry in the renderer object. A `forNames`
 * helper is exported for convenience.
 */
export interface ToolRendererRegistryValue {
  renderers: Record<string, ToolRenderer>
  loadingRenderers: Record<string, ToolLoadingRenderer>
  options?: ToolRendererOptions
}

const ToolRendererRegistryContext =
  createContext<ToolRendererRegistryValue | null>(null)

export interface ToolRendererProviderProps {
  renderers?: Record<string, ToolRenderer>
  loadingRenderers?: Record<string, ToolLoadingRenderer>
  options?: ToolRendererOptions
  children: ReactNode
}

export function ToolRendererProvider({
  renderers,
  loadingRenderers,
  options,
  children,
}: ToolRendererProviderProps) {
  const value: ToolRendererRegistryValue = {
    renderers: renderers ?? {},
    loadingRenderers: loadingRenderers ?? {},
    options,
  }
  return (
    <ToolRendererRegistryContext.Provider value={value}>
      {children}
    </ToolRendererRegistryContext.Provider>
  )
}

/**
 * Returns the context registry, or `null` if no provider is in
 * the tree. Callers handle the null case by falling back to the
 * module-scoped registry from `agent-chat/tool-registry`.
 */
export function useToolRendererRegistry(): ToolRendererRegistryValue | null {
  return useContext(ToolRendererRegistryContext)
}

/**
 * Convenience: build a `Record<string, T>` from a list of aliases
 * sharing one renderer.
 *
 *   ...forNames(['getBookings', 'get-bookings'], bookingsRenderer)
 */
export function forNames<T>(
  names: string[],
  renderer: T,
): Record<string, T> {
  return Object.fromEntries(names.map((name) => [name, renderer]))
}
